import { useEffect, useState, useContext } from 'react';
import {
  Box, Typography, List, ListItemButton, ListItemText,
  Collapse, CircularProgress, IconButton, Tooltip, Divider
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import QuizIcon from '@mui/icons-material/Quiz';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { FaRegFilePdf, FaFilePowerpoint, FaRegFileExcel, FaFileWord, FaFile, FaFolderOpen } from 'react-icons/fa';
import { ModeContext } from './ModeContext';
import FileDetailModal from './FileDetailModal';
import FileDetailModalFaq from './FileDetailModalFaq';
import { LoadToCosmos } from './SendToCosmos';

const FilelistDisplay = () => {
  const { fileList, setFileList, selectedFile, setSelectedFile, loginInfo } = useContext(ModeContext);
  const [openCategories, setOpenCategories] = useState({});
  const [fileModalType, setFileModalType] = useState(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // con-concierge-fileからファイル・FAQ両方を取得
  const reloadFiles = async () => {
    try {
      const grouped = {};

      const res = await LoadToCosmos({ id: 'bu', bu:loginInfo.compId, container: 'con-concierge-file' });
      if (res?.item && Array.isArray(res.item)) {
        res.item
          .filter((doc) => doc.bu === loginInfo.compId && doc.status !== 'delete')
          .filter((doc) => {
            // filter2が「自分のみ」のドキュメントは登録者本人(filter4)のみ表示
            if (doc.filter2 === 'MY_ONLY') {
              console.log('[FilelistDisplay MY_ONLY判定]', { docId: doc.id, filter4: doc.filter4, loginUserId: loginInfo.userId, match: doc.filter4 === loginInfo.userId });
              return doc.filter4 === loginInfo.userId;
            }
            return true;
          })
          .forEach((doc) => {
            const category = doc.category || '未分類';
            if (!grouped[category]) grouped[category] = [];
            grouped[category].push({
              id: doc.id, title: doc.filename, url: doc.url,
              messages: { category }, raw: doc,
            });
          });
      }
      setFileList(grouped);
    } catch (err) {
      console.error('ファイル再取得エラー:', err);
    }
  };
  useEffect(() => {
    if (loginInfo?.compId) {
      setLoading(true);
      reloadFiles().finally(() => setLoading(false));
    }
  }, [loginInfo]);

  const hasItems = Object.keys(fileList).length > 0;

  const toggleCategory = (category) => {
    setOpenCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
    setFileModalType('edit');
    setIsFileModalOpen(true);
  };

  const handleAddFileInCategory = (category, e) => {
    e.stopPropagation();
    setSelectedFile({ raw: { filter1: category } });
    setFileModalType('new');
    setIsFileModalOpen(true);
  };

  const handleAddFaqInCategory = (category, e) => {
    e.stopPropagation();
    setSelectedFaq({ category });
    setIsFaqModalOpen(true);
  };

  const handleFaqClick = (faq) => {
    setSelectedFaq(faq);
    setIsFaqModalOpen(true);
  };

  const getFileIcon = (filename) => {
    if (!filename) return <FaFile style={{ marginRight: 8 }} />;
    if (filename.endsWith('.pdf')) return <FaRegFilePdf size={20} style={{ marginRight: 8, color: '#D24726' }} />;
    if (filename.endsWith('.xls') || filename.endsWith('.xlsx')) return <FaRegFileExcel size={20} style={{ marginRight: 8, color: 'green' }} />;
    if (filename.endsWith('.doc') || filename.endsWith('.docx')) return <FaFileWord size={20} style={{ marginRight: 8, color: 'blue' }} />;
    if (filename.endsWith('.ppt') || filename.endsWith('.pptx')) return <FaFilePowerpoint size={20} style={{ marginRight: 8, color: '#D24726' }} />;
    return <FaFile style={{ marginRight: 8 }} />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Typography variant="h6" component="h1" sx={{ mb: 1, color: '#1976d2' }}>
        ファイル / FAQ 一覧
      </Typography>

      {!hasItems ? (
        <Typography variant="body2" sx={{ px: 2, py: 1, color: '#888' }}>
          登録されていません。サイドバーの「ファイル追加」「FAQ登録」から追加してください。
        </Typography>
      ) : (
        <List dense>
          <Divider />
          {Object.entries(fileList).map(([category, items]) => (
            <Box key={category}>
              <ListItemButton onClick={() => toggleCategory(category)}>
                <FaFolderOpen style={{ marginRight: 8, color: '#FFCC00', fontSize: '24px' }} />
                <ListItemText
                  primary={
                    <Typography variant="body1" fontWeight="bold">
                      {category}
                      <Typography component="span" variant="body2" sx={{ ml: 1, color: '#888' }}>
                        ({items.length}件)
                      </Typography>
                    </Typography>
                  }
                />
                <Tooltip title="ファイル追加" arrow>
                  <IconButton size="small" sx={{ mr: 0.5 }} onClick={(e) => handleAddFileInCategory(category, e)}>
                    <UploadFileIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="FAQ登録" arrow>
                  <IconButton size="small" sx={{ mr: 0.5 }} onClick={(e) => handleAddFaqInCategory(category, e)}>
                    <QuizIcon fontSize="small" sx={{ color: '#1976d2' }} />
                  </IconButton>
                </Tooltip>
                {openCategories[category] ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Divider />
              <Collapse in={openCategories[category]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {items.map((item) => {
                    const filter5 = item.raw?.filter5 || item.filter5;
                    const isFaq = filter5 === 'u_faq' || item._kind === 'faq';

                    return isFaq ? (
                      <ListItemButton key={`faq-${item.id}`} sx={{ pl: 4, py: 0.5 }} onClick={() => handleFaqClick(item)}>
                        <QuizIcon sx={{ mr: 1, color: '#1976d2', fontSize: 20 }} />
                        <ListItemText
                          primary={item.title || item.category || '無題'}
                          secondary={item.content ? item.content.substring(0, 50) + (item.content.length > 50 ? '...' : '') : ''}
                          slotProps={{ primary: { fontSize: 13, sx: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' } } }}
                        />
                      </ListItemButton>
                    ) : (
                      <ListItemButton key={`file-${item.id}`} sx={{ pl: 4, py: 0.5 }} onClick={() => handleFileClick(item)}>
                        {getFileIcon(item.title)}
                        <ListItemText
                          primary={item.title}
                          slotProps={{ primary: { fontSize: 13, sx: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' } } }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          ))}
        </List>
      )}

      <FileDetailModal
        open={isFileModalOpen}
        onClose={() => setIsFileModalOpen(false)}
        fileData={selectedFile}
        type={fileModalType}
        onUpdated={reloadFiles}
      />

      <FileDetailModalFaq
        open={isFaqModalOpen}
        onClose={() => { setIsFaqModalOpen(false); setSelectedFaq(null); }}
        faqData={selectedFaq}
        type={selectedFaq?.id ? 'edit' : 'new'}
        onUpdated={reloadFiles}
      />
    </Box>
  );
};

export default FilelistDisplay;
